import axios from "axios";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router";

type RedirectState = "loading" | "redirecting" | "not-found" | "error";

function Checkoutv1Redirect() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const [state, setState] = useState<RedirectState>("loading");
  const [showFallback, setShowFallback] = useState(false);

  const fetchAndRedirect = async () => {
    if (!id) {
      setState("not-found");
      return;
    }

    try {
      const response = await axios.get(
        `${import.meta.env.VITE_API_URL}/api/automatedSaleTemplates/generateStripeLink/${id}`,
      );
      const { checkoutLink } = response.data as { checkoutLink: string };
      setState("redirecting");
      window.location.replace(checkoutLink);
    } catch (err) {
      if (axios.isAxiosError(err) && err.response?.status === 404) {
        setState("not-found");
      } else {
        setState("error");
      }
    }
  };

  useEffect(() => {
    fetchAndRedirect();

    const timer = setTimeout(() => {
      setShowFallback(true);
    }, 4000);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  if (state === "not-found") {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-gray-900">404</h1>
          <p className="mt-2 text-gray-600">
            {t("checkoutv1.redirect.error")}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-gray-50">
      {state === "error" ? (
        <div className="flex flex-col items-center gap-4 text-center">
          <p className="text-gray-600">{t("checkoutv1.redirect.error")}</p>
          <button
            onClick={() => {
              setState("loading");
              fetchAndRedirect();
            }}
            className="rounded-xl bg-[#eb5e27] px-8 py-3 font-medium text-white transition-colors hover:bg-[#d4531f]"
          >
            {t("checkoutv1.redirect.button")}
          </button>
        </div>
      ) : (
        <div
          className={`flex flex-col items-center gap-4 text-center transition-opacity duration-300 ${
            showFallback ? "opacity-100" : "opacity-0"
          }`}
        >
          <p className="px-6 font-[Poppins] text-lg font-medium text-gray-700">
            {t("checkoutv1.redirect.text")}
          </p>
          <button
            onClick={fetchAndRedirect}
            className="rounded-xl bg-[#eb5e27] px-8 py-3 font-medium text-white transition-colors hover:bg-[#d4531f]"
          >
            {t("checkoutv1.redirect.button")}
          </button>
        </div>
      )}
    </div>
  );
}

export { Checkoutv1Redirect };
